import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMerchantStore } from '../store/useMerchantStore';
import { RouteDetailHeader, StepBanner } from '../components/routeKit';
import L from 'leaflet';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import 'leaflet/dist/leaflet.css';
import { getMerchantToken, apiUrl } from '../services/api';
import { RouteSetupAccordion } from '../components/RouteSetupAccordion';
import { usePublicVehicleCatalog } from '../hooks/usePublicVehicleCatalog';
import {
  hubCoordsFromRouteResult,
  DEFAULT_MERCHANT_HUB,
  isRoundTripTour,
} from '../utils/merchantPickupLocation';
import { friendlyError } from "../utils/friendlyError";
import { bestInsertion, canJoinRoute } from "../utils/insertionPlanner";
import { t } from "../i18n";
import { PhoneLink, phoneLinkHtml } from "../components/orderKit";

const OSRM_BASE_URL = (process.env.REACT_APP_OSRM_BASE_URL || '/osrm').replace(/\/$/, '');
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function OptimizedRouteView() {
  const { state } = useLocation();
  const { routeId } = useParams();
  const navigate = useNavigate();
  // Signed-in merchant: lets the page load on refresh / direct link (no router state).
  const storeMerchantId = useMerchantStore((st) => st.merchantId);

  const [result, setResult] = useState(state?.result || {});
  const [selectedOrders, setSelectedOrders] = useState(state?.selectedOrders || []);
  const [loading, setLoading] = useState(!state?.result);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedError, setUnassignedError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [splitReoptimizeBusyOrderId, setSplitReoptimizeBusyOrderId] = useState(null);
  const [splitReoptimizeRemoveAllBusy, setSplitReoptimizeRemoveAllBusy] = useState(false);

  const { vehicleTypes, vehicleStandards, catalogError, DEFAULT_VEHICLE_TYPE } = usePublicVehicleCatalog();
  const [draftVehicleType, setDraftVehicleType] = useState(DEFAULT_VEHICLE_TYPE);
  const [draftVehicleStandards, setDraftVehicleStandards] = useState(() => new Set());
  const hydratedVehicleRouteIdRef = useRef(null);

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const mapInitialized = useRef(false);

  // Plan on the map: unassigned orders as dashed "ghost" pins next to the route; click one to add it.
  const unassignedMarkersRef = useRef([]);
  const [showUnassignedOnMap, setShowUnassignedOnMap] = useState(true);
  const [pickedUnassignedId, setPickedUnassignedId] = useState(null);

  const authFetch = useCallback(async (path, options = {}) => {
    const token = getMerchantToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(apiUrl(path), { ...options, headers });
  }, []);

  const merchantId = useMemo(() => {
    return state?.merchantId ||
           storeMerchantId ||
           result?.merchantId ||
           selectedOrders[0]?.merchantId ||
           selectedOrders[0]?.merchant?.id;
  }, [state, selectedOrders, result, storeMerchantId]);

  const splitSuggestionIdSet = useMemo(() => {
    const a = result.splitStopSuggestions;
    if (!Array.isArray(a)) return new Set();
    return new Set(a.map((s) => s.orderId));
  }, [result.splitStopSuggestions]);

  // Fetch route details if not provided in state
  useEffect(() => {
    if (!routeId) return;
    
    if (state?.result && state.result.routeId === parseInt(routeId)) {
      setLoading(false);
      return;
    }

    async function fetchRouteDetails() {
      setLoading(true);
      setError(null);
      
      try {
        console.log('Fetching route details for:', routeId);
        
        const res = await authFetch(`/api/orders/routes/${routeId}/details`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const routeData = await res.json();
        console.log('Route details:', routeData);
        
        const merchantIdFromState = state?.merchantId ?? storeMerchantId;
        
        if (merchantIdFromState) {
          const ordersRes = await authFetch(`/api/orders?merchantId=${merchantIdFromState}`);
          if (!ordersRes.ok) throw new Error(`HTTP ${ordersRes.status}`);
          const allOrders = await ordersRes.json();
          
          const routeOrders = allOrders.filter(order => 
            routeData.orderedOrderIds?.includes(order.id)
          );
          
          setSelectedOrders(routeOrders);
        }
        
        setResult(routeData);
      } catch (err) {
        console.error('Failed to fetch route details:', err);
        setError(friendlyError(err, t("routes.open_failed")));
      } finally {
        setLoading(false);
      }
    }
    
    fetchRouteDetails();
  }, [routeId, state, storeMerchantId]);

  // Fetch unassigned orders
  useEffect(() => {
    if (!merchantId) {
      setUnassignedError(t("draft.err_no_merchant"));
      return;
    }

    setUnassignedLoading(true);
    setUnassignedError(null);

    async function fetchUnassigned() {
      try {
        const res = await authFetch(`/api/orders/unassigned?merchantId=${merchantId}`);
        if (!res.ok) throw new Error(t("draft.unassigned_load_failed"));
        const data = await res.json();
        setUnassignedOrders(data || []);
      } catch (err) {
        console.error('Failed to load unassigned orders:', err);
        setUnassignedError(friendlyError(err, t("draft.unassigned_load_failed")));
      } finally {
        setUnassignedLoading(false);
      }
    }

    fetchUnassigned();
  }, [merchantId]);

  useEffect(() => {
    setDraftVehicleType((prev) => {
      const codes = new Set(vehicleTypes.map((x) => x.code).filter(Boolean));
      if (codes.has(prev)) return prev;
      return vehicleTypes[0]?.code || DEFAULT_VEHICLE_TYPE;
    });
  }, [vehicleTypes, DEFAULT_VEHICLE_TYPE]);

  useEffect(() => {
    hydratedVehicleRouteIdRef.current = null;
  }, [routeId]);

  const requiredStandardsSignature = useMemo(() => {
    const arr = result.requiredVehicleStandards;
    if (!Array.isArray(arr) || arr.length === 0) return '';
    return [...arr].map(String).sort().join(',');
  }, [result.requiredVehicleStandards]);

  useEffect(() => {
    if (loading || result.routeId == null) return;
    const rid = String(result.routeId);
    if (hydratedVehicleRouteIdRef.current === rid) return;
    hydratedVehicleRouteIdRef.current = rid;

    const codes = new Set(vehicleTypes.map((x) => x.code).filter(Boolean));
    const vt = result.vehicleType;
    if (vt && codes.has(vt)) {
      setDraftVehicleType(vt);
    } else if (vt) {
      setDraftVehicleType(vt);
    } else {
      setDraftVehicleType((prev) =>
        codes.has(prev) ? prev : vehicleTypes[0]?.code || DEFAULT_VEHICLE_TYPE
      );
    }

    const arr = result.requiredVehicleStandards;
    setDraftVehicleStandards(Array.isArray(arr) ? new Set(arr.map(String)) : new Set());
  }, [
    loading,
    result.routeId,
    result.vehicleType,
    requiredStandardsSignature,
    vehicleTypes,
    DEFAULT_VEHICLE_TYPE,
  ]);

  function toggleDraftVehicleStandard(code) {
    if (!code) return;
    setDraftVehicleStandards((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function clearDraftVehicleStandards() {
    setDraftVehicleStandards(new Set());
  }

  const { orderedOrders, allPoints } = useMemo(() => {
    if (!result.orderedOrderIds?.length) {
      return { orderedOrders: [], allPoints: [] };
    }

    const orders = result.orderedOrderIds
      .map(id => selectedOrders.find(o => o.id === id))
      .filter(Boolean);

    const hub = hubCoordsFromRouteResult(result);
    const points = [
      hub,
      ...orders.map(o => [
        parseFloat(o?.deliveryLatitude),
        parseFloat(o?.deliveryLongitude),
      ]).filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng)),
      ...(isRoundTripTour(result) ? [hub] : []),
    ];

    return { orderedOrders: orders, allPoints: points };
  }, [result, selectedOrders]);

  // The full-screen loading state (below) hides the map <div>; initialise the map once it is on the page.
  const mapHostMounted = !(loading && !mapReady && !result.orderedOrderIds?.length);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || leafletMap.current || mapInitialized.current) return;

    console.log('Initializing map');
    mapInitialized.current = true;
    
    setTimeout(() => {
      if (!mapRef.current) return;
      
      leafletMap.current = L.map(mapRef.current).setView(
        allPoints[0] || [
          DEFAULT_MERCHANT_HUB.lat,
          DEFAULT_MERCHANT_HUB.lng,
        ],
        13
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(leafletMap.current);

      setTimeout(() => {
        if (leafletMap.current) {
          leafletMap.current.invalidateSize();
          setMapReady(true);
        }
      }, 100);
    }, 100);

    return () => {
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
        mapInitialized.current = false;
        setMapReady(false);
      }
    };
  // Re-run once the loading screen is gone: on a direct link / refresh the map <div> isn't mounted on first render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapHostMounted]);

  // Handle map resize when points change
  useEffect(() => {
    if (!leafletMap.current || !mapReady) return;

    console.log('Invalidating map size');
    leafletMap.current.invalidateSize();
    drawRoutesAndMarkers();

  }, [allPoints, mapReady, orderedOrders]);

  const drawRoutesAndMarkers = useCallback(async () => {
    if (!leafletMap.current || !mapReady) return;

    // Clear existing layers
    polylinesRef.current.forEach(p => leafletMap.current.removeLayer(p));
    polylinesRef.current = [];
    
    markersRef.current.forEach(m => leafletMap.current.removeLayer(m));
    markersRef.current = [];

    setLoading(true);
    setError(null);

    // Draw polylines
    for (let i = 0; i < allPoints.length - 1; i++) {
      const from = allPoints[i];
      const to = allPoints[i + 1];

      try {
        const url = `${OSRM_BASE_URL}/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();

        if (data.code === 'Ok' && data.routes?.[0]?.geometry) {
          const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          const pl = L.polyline(coords, { color: '#a63a3a', weight: 6, opacity: 0.9 }).addTo(
            leafletMap.current
          );
          polylinesRef.current.push(pl);
        }
      } catch (err) {
        console.error(`Route segment ${i + 1} failed`, err);
        const fallback = L.polyline([from, to], { color: 'red', weight: 4, dashArray: '5 10' }).addTo(
          leafletMap.current
        );
        polylinesRef.current.push(fallback);
      }

      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Draw markers with serial numbers
    orderedOrders.forEach((order, idx) => {
      const lat = parseFloat(order.deliveryLatitude);
      const lng = parseFloat(order.deliveryLongitude);
      if (isNaN(lat) || isNaN(lng)) return;
      const safeAddress = escapeHtml(order.deliveryAddress || t("draft.no_address"));

      // Serial number starts from 1
      const serialNumber = idx + 1;
      
      const icon = L.divIcon({
        className: 'custom-number-marker',
        html: `<div style="
          background:#a63a3a;
          color:white;
          width:36px;
          height:36px;
          border-radius:50%;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:bold;
          font-size:18px;
          border:3px solid white;
          box-shadow:0 3px 8px rgba(46, 33, 84, 0.14);
        ">${serialNumber}</div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });

      const marker = L.marker([lat, lng], { icon })
        .addTo(leafletMap.current)
        .bindPopup(`
          <strong>${escapeHtml(t("draft.stop_n", { n: serialNumber }))}</strong><br>
          ${escapeHtml(t("order.title", { id: order.id }))}<br>
          ${safeAddress}<br>
          ${order.clientPhone ? `${phoneLinkHtml(order.clientPhone)}<br>` : ""}
          ${escapeHtml(t("draft.distance_from_previous", { d: result.consecutiveDistances?.[idx] || t("common.na") }))}
        `);

      markersRef.current.push(marker);
    });

    // Add hub marker with 'H' (no serial number)
    const hubIcon = L.divIcon({
      className: 'custom-number-marker',
      html: `<div style="
        background:#2f7a57;
        color:white;
        width:36px;
        height:36px;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        font-weight:bold;
        font-size:20px;
        border:3px solid white;
        box-shadow:0 3px 8px rgba(46, 33, 84, 0.14);
      ">H</div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });

    const hubMarker = L.marker(allPoints[0], { icon: hubIcon })
      .addTo(leafletMap.current)
      .bindPopup(escapeHtml(t("draft.hub_popup")));

    markersRef.current.push(hubMarker);

    // Fit bounds
    if (polylinesRef.current.length > 0) {
      const bounds = L.latLngBounds(polylinesRef.current.flatMap(p => p.getLatLngs()));
      leafletMap.current.fitBounds(bounds, { padding: [60, 60] });
    }

    setLoading(false);
  }, [allPoints, orderedOrders, mapReady, result.consecutiveDistances]);

  const isDraft = (result.status || 'DRAFT') === 'DRAFT';

  const pinnedUnassigned = useMemo(
    () =>
      unassignedOrders.filter((o) => {
        const lat = parseFloat(o.deliveryLatitude);
        const lng = parseFloat(o.deliveryLongitude);
        return Number.isFinite(lat) && Number.isFinite(lng);
      }),
    [unassignedOrders]
  );

  const pickedUnassigned = useMemo(
    () => unassignedOrders.find((o) => o.id === pickedUnassignedId) || null,
    [unassignedOrders, pickedUnassignedId]
  );

  const pickedPlan = useMemo(() => {
    if (!pickedUnassigned) return null;
    return {
      canAdd: canJoinRoute(pickedUnassigned, orderedOrders),
      best: bestInsertion(hubCoordsFromRouteResult(result), orderedOrders, pickedUnassigned, isRoundTripTour(result)),
    };
  }, [pickedUnassigned, orderedOrders, result]);

  // Ghost pins live in their own layer so they redraw without re-fetching the road geometry.
  useEffect(() => {
    const map = leafletMap.current;
    if (!map || !mapReady) return undefined;
    unassignedMarkersRef.current.forEach((m) => map.removeLayer(m));
    unassignedMarkersRef.current = [];
    if (!isDraft || !showUnassignedOnMap) return undefined;

    pinnedUnassigned.forEach((o) => {
      const blocked = !canJoinRoute(o, orderedOrders);
      const picked = o.id === pickedUnassignedId;
      let cls = 'plan-ghost-pin';
      if (blocked) cls += ' is-blocked';
      if (picked) cls += ' is-picked';
      const icon = L.divIcon({
        className: 'plan-ghost-pin-root',
        html: `<div class="${cls}" title="${escapeHtml(t('order.title', { id: o.id }))}"><span class="plan-ghost-pin__plus">+</span>${o.id}</div>`,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
      });
      const marker = L.marker([parseFloat(o.deliveryLatitude), parseFloat(o.deliveryLongitude)], {
        icon,
        zIndexOffset: picked ? 1000 : -100,
        keyboard: true,
      })
        .on('click', () => setPickedUnassignedId(o.id))
        .addTo(map);
      unassignedMarkersRef.current.push(marker);
    });

    const clearPick = () => setPickedUnassignedId(null);
    map.on('click', clearPick);
    return () => map.off('click', clearPick);
  }, [mapReady, isDraft, showUnassignedOnMap, pinnedUnassigned, pickedUnassignedId, orderedOrders]);

  /** Zoom out so the route and every pinned unassigned order are in view. */
  function fitRouteAndUnassigned() {
    const map = leafletMap.current;
    if (!map) return;
    const pts = [
      ...allPoints,
      ...pinnedUnassigned.map((o) => [parseFloat(o.deliveryLatitude), parseFloat(o.deliveryLongitude)]),
    ];
    if (pts.length > 0) map.fitBounds(L.latLngBounds(pts), { padding: [60, 60], maxZoom: 15 });
  }

  function toggleUnassignedOnMap() {
    const next = !showUnassignedOnMap;
    setShowUnassignedOnMap(next);
    if (next) {
      setTimeout(fitRouteAndUnassigned, 0);
    } else {
      setPickedUnassignedId(null);
      // Back to the deliverable route on its own.
      if (allPoints.length > 0) leafletMap.current?.fitBounds(L.latLngBounds(allPoints), { padding: [60, 60], maxZoom: 15 });
    }
  }

  /** From the list: reveal the order's pin on the map and open its card. */
  function locateUnassigned(order) {
    const lat = parseFloat(order.deliveryLatitude);
    const lng = parseFloat(order.deliveryLongitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setShowUnassignedOnMap(true);
    setPickedUnassignedId(order.id);
    leafletMap.current?.flyTo([lat, lng], Math.max(leafletMap.current.getZoom(), 14), { duration: 0.6 });
    if (window.innerWidth <= 1000) mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function spotLabel(position) {
    if (position === 0) return t('plan.spot_first');
    if (position >= orderedOrders.length) return t('plan.spot_last');
    return t('plan.spot_between', { a: position, b: position + 1 });
  }

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (leafletMap.current) {
        leafletMap.current.invalidateSize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading && !mapReady && !result.orderedOrderIds?.length) {
    return (
      <div style={{ padding: '20px', color: '#1b1530', textAlign: 'center', background: '#f8f6fc', minHeight: '100vh' }}>
        <h2>{t("draft.loading")}</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#1b1530', textAlign: 'center', background: '#f8f6fc', minHeight: '100vh' }}>
        <h2>{t("draft.error_loading")}</h2>
        <p style={{ color: '#a63a3a' }}>{error}</p>
        <button 
          onClick={() => navigate("/planned-routes")}
          style={{
            padding: '10px 20px',
            background: '#e6e1f0',
            color: '#1b1530',
            border: 'none',
            borderRadius: '14px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
{t("draft.back_to_planned")}
        </button>
      </div>
    );
  }

  if (!result.orderedOrderIds?.length) {
    return (
      <div style={{ padding: '20px', color: '#1b1530', textAlign: 'center', background: '#f8f6fc', minHeight: '100vh' }}>
        <h2>{t("draft.no_result")}</h2>
        <button 
          onClick={() => navigate("/planned-routes")}
          style={{
            padding: '10px 20px',
            background: '#e6e1f0',
            color: '#1b1530',
            border: 'none',
            borderRadius: '14px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
{t("draft.back_to_planned")}
        </button>
      </div>
    );
  }

  const onDragStart = () => {
    console.log('Drag started');
  };

  const onDragEnd = async (dragResult) => {
    console.log('Drag ended:', dragResult);

    if (!dragResult.destination) {
      console.log('No destination - drag cancelled');
      return;
    }

    if (!result.routeId) {
      setActionError(t("draft.err_no_route_id"));
      return;
    }

    const items = Array.from(orderedOrders.map(o => o.id));
    const [reordered] = items.splice(dragResult.source.index, 1);
    items.splice(dragResult.destination.index, 0, reordered);

    console.log('New order sequence:', items);

    try {
      const res = await authFetch(`/api/orders/routes/${result.routeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          newOrder: items,
          distanceMatrix: result.distanceMatrix || {},
        }),
      });

      const responseText = await res.text();

      if (!res.ok) {
        throw new Error(responseText || t("draft.reorder_failed_short"));
      }

      let updated;
      try {
        updated = JSON.parse(responseText);
        console.log('Updated route:', updated);
      } catch (jsonErr) {
        console.error('Failed to parse JSON response:', jsonErr);
        throw new Error(t("draft.invalid_response"));
      }

      setResult(updated);
      
      const updatedOrders = updated.orderedOrderIds
        .map(id => selectedOrders.find(o => o.id === id) || unassignedOrders.find(o => o.id === id))
        .filter(Boolean);
      setSelectedOrders(updatedOrders);
      
      setActionError(null);

      const unRes = await authFetch(`/api/orders/unassigned?merchantId=${merchantId}`);
      if (unRes.ok) {
        const freshUnassigned = await unRes.json();
        setUnassignedOrders(freshUnassigned);
      }

    } catch (err) {
      console.error('Reorder failed:', err);
      setActionError(t("draft.reorder_failed", { reason: friendlyError(err) }));
    }
  };

  const handleRemoveSuggestedAndReoptimize = async (orderId) => {
    if (!result.routeId) {
      setActionError(t("draft.err_no_route_id"));
      return;
    }
    if ((result.status || 'DRAFT') !== 'DRAFT') {
      setActionError(t("draft.reoptimize_draft_only"));
      return;
    }
    setSplitReoptimizeBusyOrderId(orderId);
    setSplitReoptimizeRemoveAllBusy(false);
    setActionError(null);
    setLoading(true);
    try {
      const patchBody = {
        removeOrderId: orderId,
        reoptimize: true,
        distanceMatrix: result.distanceMatrix || {},
      };
      console.log('[OptimizedRouteView] split remove+reoptimize', result.routeId, patchBody, 'orderedOrderIds=', result.orderedOrderIds);
      const res = await authFetch(`/api/orders/routes/${result.routeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patchBody),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }
      const updated = await res.json();
      console.log('[OptimizedRouteView] split remove+reoptimize response orderedOrderIds=', updated.orderedOrderIds);
      setResult(updated);
      setSelectedOrders((prev) => prev.filter((o) => o.id !== orderId));
      const unRes = await authFetch(`/api/orders/unassigned?merchantId=${merchantId}`);
      if (unRes.ok) {
        setUnassignedOrders(await unRes.json());
      }
    } catch (err) {
      console.error('Remove and re-optimize failed:', err);
      setActionError(friendlyError(err, t("draft.remove_reoptimize_failed")));
    } finally {
      setSplitReoptimizeBusyOrderId(null);
      setSplitReoptimizeRemoveAllBusy(false);
      setLoading(false);
    }
  };

  const handleRemoveAllSuggestedAndReoptimize = async () => {
    if (!result.routeId) {
      setActionError(t("draft.err_no_route_id"));
      return;
    }
    if ((result.status || 'DRAFT') !== 'DRAFT') {
      setActionError(t("draft.reoptimize_draft_only"));
      return;
    }
    const ids = (result.splitStopSuggestions || []).map((s) => s.orderId).filter((id) => id != null);
    if (ids.length < 2) {
      return;
    }
    setSplitReoptimizeRemoveAllBusy(true);
    setSplitReoptimizeBusyOrderId(null);
    setActionError(null);
    setLoading(true);
    try {
      const patchBody = {
        removeOrderIds: ids,
        reoptimize: true,
        distanceMatrix: result.distanceMatrix || {},
      };
      console.log('[OptimizedRouteView] split remove ALL suggested', result.routeId, patchBody, 'orderedOrderIds=', result.orderedOrderIds);
      const res = await authFetch(`/api/orders/routes/${result.routeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patchBody),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }
      const updated = await res.json();
      console.log('[OptimizedRouteView] split remove ALL suggested response orderedOrderIds=', updated.orderedOrderIds);
      setResult(updated);
      const idSet = new Set(ids);
      setSelectedOrders((prev) => prev.filter((o) => !idSet.has(o.id)));
      const unRes = await authFetch(`/api/orders/unassigned?merchantId=${merchantId}`);
      if (unRes.ok) {
        setUnassignedOrders(await unRes.json());
      }
    } catch (err) {
      console.error('Remove all suggested failed:', err);
      setActionError(friendlyError(err, t("draft.remove_suggested_failed")));
    } finally {
      setSplitReoptimizeRemoveAllBusy(false);
      setLoading(false);
    }
  };

  const handleRemove = async (orderId) => {
    if (!result.routeId) {
      setActionError(t("draft.err_no_route_id"));
      return;
    }

    setLoading(true);

    try {
      const res = await authFetch(`/api/orders/routes/${result.routeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          removeOrderId: orderId,
          distanceMatrix: result.distanceMatrix || {},
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }

      const updated = await res.json();
      console.log('Updated route after remove:', updated);
      
      setResult(updated);
      setSelectedOrders(prev => prev.filter(o => o.id !== orderId));
      setActionError(null);

      const unRes = await authFetch(`/api/orders/unassigned?merchantId=${merchantId}`);
      if (unRes.ok) {
        setUnassignedOrders(await unRes.json());
      }
    } catch (err) {
      console.error('Remove failed:', err);
      setActionError(t("draft.remove_failed", { reason: friendlyError(err) }));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (orderId, position = null) => {
    if (!result.routeId) {
      setActionError(t("draft.err_no_route_id"));
      return;
    }

    setActionError(null);
    setLoading(true);

    try {
      console.log('Adding order:', orderId, 'at position:', position ?? orderedOrders.length);
      
      const res = await authFetch(`/api/orders/routes/${result.routeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          addOrderId: orderId,
          insertPosition: position ?? orderedOrders.length,
          distanceMatrix: result.distanceMatrix,
        }),
      });

      const responseText = await res.text();
      console.log('Add response:', responseText);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${responseText}`);
      }

      let updated;
      try {
        updated = JSON.parse(responseText);
        console.log('Updated route after add:', updated);
      } catch (jsonErr) {
        console.error('Failed to parse JSON response:', jsonErr);
        throw new Error(t("draft.invalid_response"));
      }

      // Find the newly added order from unassigned list
      const newOrder = unassignedOrders.find(o => o.id === orderId);
      
      // Update result with new route data (includes updated matrix and distances)
      setResult(updated);
      
      // Update selected orders with the new order
      if (newOrder) {
        setSelectedOrders(prev => {
          const newOrders = [...prev];
          const insertAt = position ?? prev.length;
          newOrders.splice(insertAt, 0, newOrder);
          return newOrders;
        });
      }

      // Refresh unassigned list
      const unRes = await authFetch(`/api/orders/unassigned?merchantId=${merchantId}`);
      if (unRes.ok) {
        const freshUnassigned = await unRes.json();
        setUnassignedOrders(freshUnassigned);
      }

      setActionError(null);

    } catch (err) {
      console.error('Add failed:', err);
      setActionError(t("draft.add_failed", { reason: friendlyError(err) }));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!result.routeId) {
      setActionError(t("draft.err_no_route_id"));
      return;
    }

    try {
      const res = await authFetch(`/api/orders/routes/${result.routeId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicleType: draftVehicleType,
          requiredVehicleStandards: Array.from(draftVehicleStandards),
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }

      setActionError(null);
      // Move straight on to the next step: the confirmed route, where it is broadcast to drivers.
      navigate(`/confirmed-route/${result.routeId}`, {
        state: {
          merchantId,
          flash: {
            title: t("draft.confirmed_flash", { id: result.routeId }),
            next: t("draft.confirmed_next"),
          },
        },
      });
    } catch (err) {
      console.error('Confirm failed:', err);
      setActionError(t("draft.confirm_failed", { reason: friendlyError(err) }));
    }
  };

  return (
    <div className="route-view">
      {/* Left panel */}
      <div className="route-view__panel">
        <RouteDetailHeader
          routeId={routeId}
          eyebrow={t("draft.eyebrow")}
          backTo="/planned-routes"
          backLabel={t("nav.planned_routes")}
          status={result.status || "DRAFT"}
          route={result}
        />
        <StepBanner flash={state?.flash} />

        {Array.isArray(result.splitStopSuggestions) && result.splitStopSuggestions.length > 0 && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 14px',
              background: '#faf1df',
              border: '1px solid #94490f',
              borderRadius: '10px',
              color: '#8a6420',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <div style={{ fontWeight: 700, color: '#8a6420', flex: 1 }}>
{t("draft.consider_separate")}
              </div>
              {(result.status || 'DRAFT') === 'DRAFT' && result.splitStopSuggestions.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveAllSuggestedAndReoptimize()}
                  disabled={loading || splitReoptimizeBusyOrderId !== null || splitReoptimizeRemoveAllBusy}
                  style={{
                    flexShrink: 0,
                    padding: '6px 10px',
                    borderRadius: '14px',
                    border: '1px solid #8a6420',
                    background: splitReoptimizeRemoveAllBusy ? '#e6e1f0' : '#f3effb',
                    color: '#8a6420',
                    cursor: loading || splitReoptimizeBusyOrderId !== null || splitReoptimizeRemoveAllBusy ? 'wait' : 'pointer',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {splitReoptimizeRemoveAllBusy ? t("draft.reoptimizing") : t("draft.remove_all_flagged")}
                </button>
              )}
            </div>
            {result.splitStopSuggestions.length > 1 && (
              <p style={{ margin: '0 0 10px', fontSize: '13px', lineHeight: 1.45, color: '#8a6420' }}>
                {t("draft.several_stops")}
              </p>
            )}
            <ul style={{ margin: 0, paddingLeft: '18px', listStyleType: 'disc' }}>
              {result.splitStopSuggestions.map((s) => (
                <li key={s.orderId} style={{ marginBottom: '8px', lineHeight: 1.4, listStylePosition: 'outside' }}>
                  {s.message || t("order.title", { id: s.orderId })}
                </li>
              ))}
            </ul>
            {(result.status || 'DRAFT') === 'DRAFT' && (
              <p style={{ margin: '10px 0 0', fontSize: '12px', fontStyle: 'italic', color: '#e8d3a8' }}>
{t("draft.use_minus")}
              </p>
            )}
          </div>
        )}

        {actionError && (
          <div
            style={{
              background: '#a63a3a',
              color: '#ffffff',
              padding: '12px',
              borderRadius: '14px',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            {actionError}
          </div>
        )}

        <h3 style={{ color: '#1b1530' }}>{t("draft.sequence", { n: orderedOrders.length })}</h3>

        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Droppable droppableId="route">
            {(provided) => (
              <ol
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  paddingLeft: '0',
                  margin: 0,
                  minHeight: '50px',
                }}
              >
                {orderedOrders.map((order, index) => (
                  <Draggable
                    key={order.id.toString()}
                    draggableId={order.id.toString()}
                    index={index}
                    isDragDisabled={loading || splitReoptimizeBusyOrderId !== null || splitReoptimizeRemoveAllBusy}
                  >
                    {(provided, snapshot) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          margin: '12px 0',
                          padding: '12px',
                          background: snapshot.isDragging ? '#fcfbfe' : '#ffffff',
                          borderRadius: '14px',
                          border: '1px solid #e6e1f0',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s ease',
                          boxShadow: snapshot.isDragging ? '0 8px 20px rgba(46, 33, 84, 0.14)' : 'none',
                          transform: snapshot.isDragging ? 'scale(1.02)' : 'scale(1)',
                          opacity: loading && !snapshot.isDragging ? 0.6 : 1,
                          ...provided.draggableProps.style,
                        }}
                      >
                        {/* Serial Number Badge */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            background: '#a63a3a',
                            color: '#ffffff',
                            borderRadius: '50%',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            marginRight: '12px',
                            border: '2px solid white',
                            boxShadow: '0 2px 5px rgba(46, 33, 84, 0.14)',
                          }}
                        >
                          {index + 1}
                        </div>

                        <span
                          {...provided.dragHandleProps}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            marginRight: '16px',
                            background: snapshot.isDragging ? '#f1eef8' : '#fcfbfe',
                            borderRadius: '14px',
                            cursor: loading ? 'wait' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                            userSelect: 'none',
                            transition: 'background 0.2s',
                            opacity: loading ? 0.5 : 1,
                            ...provided.dragHandleProps?.style,
                          }}
                          title={t("draft.drag_to_reorder")}
                        >
                          <i
                            className="fas fa-grip-dots-vertical"
                            style={{
                              color: snapshot.isDragging ? '#1b1530' : '#6a6480',
                              fontSize: '20px',
                            }}
                          ></i>
                        </span>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <strong style={{ color: '#1b1530' }}>{t("order.title", { id: order.id })}</strong>
                            <span style={{ 
                              background: '#a63a3a', 
                              color: '#ffffff', 
                              padding: '2px 8px', 
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {t("draft.stop_n", { n: index + 1 })}
                            </span>
                          </div>
                          <span style={{ color: '#6a6480' }}>
                            {order.deliveryAddress || t("draft.no_address")}
                          </span><br />
                          {order.clientPhone ? <><PhoneLink phone={order.clientPhone} /><br /></> : null}
                          <small style={{ color: '#6a6480' }}>
                            {t("draft.distance_from_previous", { d: result.consecutiveDistances?.[index] || t("common.na") })}
                          </small>
                        </div>

                        {splitSuggestionIdSet.has(order.id) && (result.status || 'DRAFT') === 'DRAFT' ? (
                          <button
                            type="button"
                            title={t("draft.remove_and_reoptimize")}
                            aria-label={t("draft.remove_and_reoptimize")}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!loading && splitReoptimizeBusyOrderId === null && !splitReoptimizeRemoveAllBusy) {
                                handleRemoveSuggestedAndReoptimize(order.id);
                              }
                            }}
                            disabled={loading || splitReoptimizeBusyOrderId !== null || splitReoptimizeRemoveAllBusy}
                            style={{
                              flexShrink: 0,
                              width: '40px',
                              height: '40px',
                              padding: 0,
                              borderRadius: '14px',
                              border: '1px solid #8a6420',
                              background:
                                splitReoptimizeBusyOrderId === order.id || splitReoptimizeRemoveAllBusy
                                  ? '#e6e1f0'
                                  : '#f3effb',
                              color: '#8a6420',
                              cursor:
                                loading || splitReoptimizeBusyOrderId !== null || splitReoptimizeRemoveAllBusy
                                  ? 'wait'
                                  : 'pointer',
                              fontSize: '22px',
                              lineHeight: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {splitReoptimizeBusyOrderId === order.id ? '…' : '−'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(order.id);
                            }}
                            disabled={loading || splitReoptimizeBusyOrderId !== null || splitReoptimizeRemoveAllBusy}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: loading ? '#a59eae' : '#a63a3a',
                              fontWeight: 'bold',
                              cursor: loading ? 'wait' : 'pointer',
                              padding: '6px 12px',
                              fontSize: '14px',
                              opacity: loading ? 0.5 : 1,
                            }}
                          >
{t("draft.remove")}
                          </button>
                        )}
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ol>
            )}
          </Droppable>
        </DragDropContext>

        <p style={{ color: '#1b1530', fontWeight: '500', marginTop: '16px' }}>
          {result.tourMode !== 'ONE_WAY' && (
            <>
              <strong>{t("draft.return_to_hub")}</strong> {result.consecutiveDistances?.[orderedOrders.length] || t("common.na")}
            </>
          )}
          {result.tourMode === 'ONE_WAY' && (
            <span className="muted">{t("draft.one_way_note")}</span>
          )}
        </p>

        <RouteSetupAccordion
          idPrefix={`route-setup-${routeId}`}
          showVehicle={(result.status || 'DRAFT') === 'DRAFT'}
          loading={loading}
          unassignedOrders={unassignedOrders}
          unassignedLoading={unassignedLoading}
          unassignedError={unassignedError}
          onAddOrder={(id) => handleAdd(id)}
          onLocateOrder={isDraft ? locateUnassigned : undefined}
          vehicleTypes={vehicleTypes}
          vehicleStandards={vehicleStandards}
          catalogError={catalogError}
          vehicleType={draftVehicleType}
          onVehicleTypeChange={setDraftVehicleType}
          requiredVehicleStandards={draftVehicleStandards}
          onToggleVehicleStandard={toggleDraftVehicleStandard}
          onClearVehicleStandards={clearDraftVehicleStandards}
        />

        <div className="route-view__actions">
          {(result.status || 'DRAFT') === 'DRAFT' ? (
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? '#a59eae' : '#2f7a57',
              color: '#ffffff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '1.1rem',
              fontWeight: '500',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t("ready.processing") : t("draft.confirm_route")}
          </button>
          ) : null}
          <button
            onClick={() => navigate('/planned-routes')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: '#ffffff',
              color: '#6b4eaa',
              border: '1px solid #cfc7e0',
              borderRadius: '14px',
              fontSize: '15px',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
{t("common.back")}
          </button>
        </div>
      </div>

      {/* Map container */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 8px 30px rgba(46, 33, 84, 0.14)',
          background: '#f1eef8',
        }}
      >
        <div 
          ref={mapRef} 
          style={{ 
            height: '100%', 
            width: '100%',
            opacity: mapReady ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        />

        {isDraft && mapReady && unassignedOrders.length > 0 ? (
          <div className="plan-map-chip">
            <span className="plan-map-chip__dot" aria-hidden="true" />
            <span className="plan-map-chip__text">
              <strong>{t('plan.chip', { n: unassignedOrders.length })}</strong>
              {showUnassignedOnMap ? (
                <small>
                  {unassignedOrders.length > pinnedUnassigned.length
                    ? t('plan.no_pin', { n: unassignedOrders.length - pinnedUnassigned.length })
                    : t('plan.hint')}
                </small>
              ) : null}
            </span>
            <button
              type="button"
              className={`plan-map-chip__toggle${showUnassignedOnMap ? ' is-on' : ''}`}
              aria-pressed={showUnassignedOnMap}
              onClick={toggleUnassignedOnMap}
            >
              {showUnassignedOnMap ? t('plan.hide') : t('plan.show')}
            </button>
          </div>
        ) : null}

        {isDraft && showUnassignedOnMap && pickedUnassigned && pickedPlan ? (
          <div className="plan-card" role="dialog" aria-label={t('order.title', { id: pickedUnassigned.id })}>
            <div className="plan-card__head">
              <span className="plan-card__pin" aria-hidden="true">+{pickedUnassigned.id}</span>
              <div className="plan-card__title">
                <strong>{t('order.title', { id: pickedUnassigned.id })}</strong>
                <span>{pickedUnassigned.deliveryAddress || t('draft.no_address')}</span>
                <PhoneLink phone={pickedUnassigned.clientPhone} />
              </div>
              <button
                type="button"
                className="plan-card__close"
                aria-label={t('common.close')}
                onClick={() => setPickedUnassignedId(null)}
              >
                ×
              </button>
            </div>

            {pickedPlan.canAdd ? (
              <>
                {pickedPlan.best ? (
                  <div className="plan-card__spot">
                    <span aria-hidden="true">🎯</span>
                    <div>
                      <strong>{spotLabel(pickedPlan.best.position)}</strong>
                      <small>{t('plan.detour', { km: pickedPlan.best.detourKm.toFixed(1) })}</small>
                    </div>
                  </div>
                ) : null}
                {(() => {
                  // Start / best spot / end; whichever matches the suggestion is the primary action.
                  const n = orderedOrders.length;
                  const bestPos = pickedPlan.best ? pickedPlan.best.position : n;
                  const choices = [
                    { key: 'start', label: t('plan.add_start'), position: 0, icon: '⇤' },
                    ...(bestPos > 0 && bestPos < n
                      ? [{ key: 'best', label: t('plan.add_best'), position: bestPos, icon: '🎯' }]
                      : []),
                    { key: 'end', label: t('draft.add_to_end'), position: n, icon: '⇥' },
                  ];
                  const primary = choices.find((c) => c.position === bestPos) || choices[choices.length - 1];
                  const secondary = choices.filter((c) => c !== primary);
                  return (
                    <div className="plan-card__actions plan-card__actions--stack">
                      <button
                        type="button"
                        className="plan-card__primary"
                        disabled={loading}
                        onClick={() => handleAdd(pickedUnassigned.id, primary.position)}
                      >
                        <span aria-hidden="true">{primary.icon}</span>{' '}
                        {loading ? t('draft.adding') : primary.label}
                      </button>
                      {n > 0 ? (
                        <div className="plan-card__secondary-row">
                          {secondary.map((c) => (
                            <button
                              key={c.key}
                              type="button"
                              className="plan-card__secondary"
                              disabled={loading}
                              onClick={() => handleAdd(pickedUnassigned.id, c.position)}
                            >
                              <span aria-hidden="true">{c.icon}</span> {c.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </>
            ) : (
              <p className="plan-card__blocked">{t('plan.cannot_direct')}</p>
            )}
          </div>
        ) : null}

        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(46, 33, 84, 0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1b1530',
              fontSize: '1.3rem',
              zIndex: 20,
            }}
          >
{t("draft.updating")}
          </div>
        )}

        {!mapReady && !loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#f1eef8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1b1530',
              fontSize: '1.3rem',
              zIndex: 20,
            }}
          >
{t("draft.initializing_map")}
          </div>
        )}
      </div>
    </div>
  );
}